import React, { useEffect, useState } from "react";

/**
 * Handover entitlement gate.
 *
 * Wraps a route so that access is controlled by the new handover session
 * (`__Host-jcc_handover_session` cookie) and the member's entitlement
 * list returned by `/api/auth/handover/me` — NOT by the legacy
 * AuthContext / JWT cookie.
 *
 * Applies in BOTH Preview and Production. The four member workspace
 * hubs use this gate exclusively; the legacy AuthContext / /login flow
 * remains in place for `/dashboard`, `/care-rooms`, and all other
 * routes wrapped in <ProtectedRoute>.
 *
 * Renders one of four states:
 *   loading       — quietly checking the session (no hub flash)
 *   needs-signin  — calm prompt with a button to /auth/handover/request
 *   missing-ent   — signed in but this hub is not in their tier
 *   ok            — renders children
 */
export default function HandoverGate({ requiredEntitlement, children }) {
  // ``requiredEntitlement`` may be a single string (legacy call sites) OR
  // an array of strings — in which case holding ANY of them satisfies the
  // gate. Kept backward-compatible so existing single-string usages
  // continue to work unchanged.
  const required = Array.isArray(requiredEntitlement)
    ? requiredEntitlement
    : (requiredEntitlement ? [requiredEntitlement] : []);
  const [state, setState] = useState("loading");
  const [ents, setEnts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/handover/me`,
          { credentials: "include" }
        );
        if (cancelled) return;
        if (!r.ok) {
          setState("needs-signin");
          return;
        }
        const body = await r.json().catch(() => ({}));
        if (!body || !body.active) {
          setState("needs-signin");
          return;
        }
        const list = Array.isArray(body.entitlements) ? body.entitlements : [];
        setEnts(list);
        const hasRequired =
          required.length === 0 || required.some((r) => list.includes(r));
        setState(hasRequired ? "ok" : "missing-ent");
      } catch (_) {
        if (!cancelled) setState("needs-signin");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requiredEntitlement]);

  if (state === "loading") {
    return (
      <main
        className="mx-auto max-w-md px-6 py-24 text-center"
        data-testid="handover-gate-loading"
      >
        <h1 className="text-lg font-semibold">Checking your access…</h1>
        <p className="mt-2 text-sm text-zinc-600">One moment please.</p>
      </main>
    );
  }

  if (state === "needs-signin") {
    return (
      <main
        className="mx-auto max-w-md px-6 py-24 text-center"
        data-testid="handover-gate-needs-signin"
      >
        <h1 className="text-2xl font-semibold">Sign in to view this hub</h1>
        <p className="mt-3 text-sm text-zinc-600">
          This area is for Just Care Community members. Use your member email to
          receive a one-time sign-in link.
        </p>
        <a
          href="/auth/handover/request"
          data-testid="handover-gate-signin-button"
          className="mt-8 inline-block rounded-md bg-zinc-900 px-5 py-2.5
                     text-sm font-medium text-white"
        >
          Send me a sign-in link
        </a>
      </main>
    );
  }

  if (state === "missing-ent") {
    return (
      <main
        className="mx-auto max-w-md px-6 py-24 text-center"
        data-testid="handover-gate-missing-entitlement"
      >
        <h1 className="text-2xl font-semibold">Not included in your access</h1>
        <p className="mt-3 text-sm text-zinc-600">
          You&rsquo;re signed in to Just Care Community, but this hub is not part
          of your current access. If you think this is a mistake, please contact
          us.
        </p>
        <a
          href="/"
          className="mt-8 inline-block rounded-md bg-zinc-900 px-5 py-2.5
                     text-sm font-medium text-white"
        >
          Back to home
        </a>
      </main>
    );
  }

  return children;
}
